import { useEffect, useState, useCallback } from 'react';
import { Alert, AlertTitle, Box, Button, Stack, Chip } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { mealBreaksApi, BreakState, TechBreakStatus } from '../services/api';

// App-wide meal-break compliance banner (California labor-law):
//  - Technicians are prompted when a meal break is due, and can record it taken
//    (or ask a manager for an exception).
//  - Managers see due/violation breaks across techs, can grant an exception, or
//    mark a break taken. A background scan notifies managers of any violation.
const MANAGER_ROLES = ['Admin', 'Manager', 'Dispatcher'];
const POLL_MS = 60000;

export default function BreakComplianceBanner() {
  const { user } = useAuth();
  const isManager = !!user && (user.is_superadmin || (user.roles || []).some(r => MANAGER_ROLES.includes(r)));
  const isTech = !!user && (user.roles || []).some(r => r === 'Technician');

  const [myBreaks, setMyBreaks] = useState<BreakState[]>([]);
  const [mgr, setMgr] = useState<TechBreakStatus[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      if (isTech) {
        const r = await mealBreaksApi.getStatus(user.id);
        setMyBreaks(r.data.breaks || []);
      }
      if (isManager) {
        try { await mealBreaksApi.scan(); } catch { /* non-fatal */ }
        const r = await mealBreaksApi.getAllStatus();
        setMgr((r.data.techs || []).filter(t => t.action_required));
      }
    } catch { /* silent */ }
  }, [user, isTech, isManager]);

  useEffect(() => {
    if (!user) return;
    refresh();
    const iv = setInterval(refresh, POLL_MS);
    return () => clearInterval(iv);
  }, [user, refresh]);

  if (!user) return null;

  const myUrgent = myBreaks.find(b => b.status === 'violation') || myBreaks.find(b => b.status === 'due');

  const take = async (techId: number, bt: string) => {
    setBusy(`take-${techId}-${bt}`);
    try { await mealBreaksApi.take(techId, bt); await refresh(); } finally { setBusy(null); }
  };
  const grant = async (techId: number, bt: string) => {
    const reason = window.prompt('Reason for meal-break exception (waiver):') || undefined;
    setBusy(`exc-${techId}-${bt}`);
    try { await mealBreaksApi.grantException(techId, bt, reason); await refresh(); } finally { setBusy(null); }
  };

  return (
    <Box sx={{ mb: myUrgent || mgr.length ? 2 : 0 }}>
      {/* Technician's own prompt */}
      {isTech && myUrgent && (
        <Alert severity={myUrgent.status === 'violation' ? 'error' : 'warning'} sx={{ mb: 1 }}
          action={
            <Button color="inherit" size="small" disabled={!!busy}
              onClick={() => take(user.id, myUrgent.break_type)}>
              I&apos;ve taken my break
            </Button>
          }>
          <AlertTitle>{myUrgent.break_type === 'meal2' ? 'Second meal break' : 'Meal break'} {myUrgent.status === 'violation' ? 'missed' : 'due'}</AlertTitle>
          {myUrgent.message} If you can&apos;t take it now, ask a manager for an exception.
        </Alert>
      )}

      {/* Manager view: techs needing attention */}
      {isManager && mgr.map(t => {
        const b = t.breaks.find(x => x.status === 'violation') || t.breaks.find(x => x.status === 'due');
        if (!b) return null;
        const violation = b.status === 'violation';
        return (
          <Alert key={t.tech_id} severity={violation ? 'error' : 'warning'} sx={{ mb: 1 }}>
            <AlertTitle>
              {t.tech_name} — {violation ? 'LABOR-LAW VIOLATION' : 'meal break due'}
              <Chip size="small" label={b.break_type === 'meal2' ? '2nd meal' : '1st meal'} sx={{ ml: 1 }} />
            </AlertTitle>
            {b.message}
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button size="small" variant="outlined" color="inherit" disabled={!!busy}
                onClick={() => grant(t.tech_id, b.break_type)}>Grant exception</Button>
              <Button size="small" variant="outlined" color="inherit" disabled={!!busy}
                onClick={() => take(t.tech_id, b.break_type)}>Mark taken</Button>
            </Stack>
          </Alert>
        );
      })}
    </Box>
  );
}
